import React, { createContext, useState, useContext } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const ModelContext = createContext(null);

export const useModel = () => {
  const context = useContext(ModelContext);
  if (!context) {
    throw new Error('useModel must be used within ModelProvider');
  }
  return context;
};

export const ModelProvider = ({ children }) => {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchModels = async (labelId) => {
    try {
      setLoading(true);
      const response = await api.get(`/models/label/${labelId}/`);
      const modelsData = response.data.results || response.data || [];
      setModels(Array.isArray(modelsData) ? modelsData : []);
      return modelsData;
    } catch (error) {
      console.error('Error fetching models:', error);
      toast.error('Failed to fetch models');
      setModels([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchModelInfo = async (modelId) => {
    try {
      setLoading(true);
      const response = await api.get(`/models/${modelId}/`);
      setSelectedModel(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching model info:', error);
      toast.error('Failed to fetch model details');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const trainModel = async (labelId, status, modelType, sensorId) => {
    try {
      setLoading(true);
      const response = await api.post(`/models/label/${labelId}/status/${status}/train/`, {
        model_type: modelType,
        sensor_id: sensorId,
      });
      toast.success('Model trained successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to train model');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const predictSamples = async (modelId, sampleIds) => {
    try {
      setLoading(true);
      const response = await api.post(`/models/${modelId}/predict/`, {
        samples_to_predict: sampleIds,
      });
      setPredictions(response.data);
      toast.success('Predictions completed');
      return response.data;
    } catch (error) {
      toast.error('Failed to make predictions');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteModel = async (modelId) => {
    try {
      await api.delete(`/models/${modelId}/`);
      toast.success('Model deleted successfully');
    } catch (error) {
      toast.error('Failed to delete model');
      throw error;
    }
  };

  const acceptPredictions = async (modelId, predictions) => {
    try {
      setLoading(true);
      const response = await api.post(`/models/${modelId}/accept-predictions/`, {
        predictions,
      });
      const msg = response.data.message || 'Predictions accepted';
      if (response.data.retrain_triggered) {
        toast.success(`${msg}. Auto-retrain started in background!`);
      } else {
        toast.success(msg);
      }
      return response.data;
    } catch (error) {
      toast.error('Failed to accept predictions');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const retrainModel = async (modelId, modelType = null) => {
    try {
      setLoading(true);
      const response = await api.post(`/models/${modelId}/retrain/`, {
        model_type: modelType,
      });
      const { comparison } = response.data;
      if (comparison?.promoted) {
        toast.success(
          `New model v${response.data.version} promoted! ` +
          `${comparison.metric}: ${comparison.old_value} -> ${comparison.new_value}`
        );
      } else if (comparison) {
        toast.info(
          `New model v${response.data.version} saved as inactive. ` +
          `${comparison.metric}: ${comparison.old_value} -> ${comparison.new_value} (not better)`
        );
      } else {
        toast.success('Model retrained successfully');
      }
      return response.data;
    } catch (error) {
      toast.error('Failed to retrain model');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const toggleModelActive = async (modelId, isActive = null) => {
    try {
      setLoading(true);
      const response = await api.post(`/models/${modelId}/toggle-active/`, {
        is_active: isActive,
      });
      const status = response.data.is_active ? 'activated' : 'deactivated';
      toast.success(`Model ${status}`);
      return response.data;
    } catch (error) {
      toast.error('Failed to toggle model status');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    models,
    setModels,
    selectedModel,
    setSelectedModel,
    predictions,
    setPredictions,
    loading,
    fetchModels,
    fetchModelInfo,
    trainModel,
    predictSamples,
    deleteModel,
    acceptPredictions,
    retrainModel,
    toggleModelActive,
  };

  return <ModelContext.Provider value={value}>{children}</ModelContext.Provider>;
};
